import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Upload,
  X,
  FileImage,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { useProcesarOCR } from '../hooks/useFacturasCompra';
import type { FacturaOCRData } from '../types/facturas';
import OCRProgressIndicator, { type OCRPhase } from './ocr/OCRProgressIndicator';

interface UploadFacturaOCRProps {
  isOpen: boolean;
  onClose: () => void;
  onDataExtracted: (data: FacturaOCRData, imageUrl?: string) => void;
}

const OCR_PHASES: OCRPhase[] = ['validating', 'uploading', 'processing', 'extracting'];
// Duración en ms para cada fase antes de pasar a la siguiente
const PHASE_DURATIONS_MS = [800, 1500, 30000, 3000];
// Tiempo total estimado para simular barra de progreso (suma de todas las fases)
const TOTAL_ESTIMADO_MS = PHASE_DURATIONS_MS.reduce((a, b) => a + b, 0);

const UploadFacturaOCR: React.FC<UploadFacturaOCRProps> = ({
  isOpen,
  onClose,
  onDataExtracted,
}) => {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ocrPhase, setOcrPhase] = useState<OCRPhase>('validating');
  const [ocrProgress, setOcrProgress] = useState(0);
  // Guardamos todos los timers de fases para poder cancelarlos
  const phaseTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const procesarOCRMutation = useProcesarOCR();

  // Limpiar todos los temporizadores activos
  const limpiarTimers = () => {
    phaseTimersRef.current.forEach((t) => clearTimeout(t));
    phaseTimersRef.current = [];
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  // Iniciar animación de fases OCR con barra de progreso simulada
  const iniciarAnimacionOCR = () => {
    limpiarTimers();
    setOcrPhase('validating');
    setOcrProgress(0);

    // Programar transiciones de fase en los tiempos acumulados
    let acumulado = 0;
    OCR_PHASES.forEach((fase, i) => {
      if (i === 0) {
        acumulado += PHASE_DURATIONS_MS[0];
        return; // La primera ya está activa
      }
      const delay = acumulado;
      acumulado += PHASE_DURATIONS_MS[i];
      const t = setTimeout(() => setOcrPhase(fase), delay);
      phaseTimersRef.current.push(t);
    });

    // Barra de progreso global simulada: avanza hasta 90%, luego espera resultado real
    let elapsedMs = 0;
    progressTimerRef.current = setInterval(() => {
      elapsedMs += 250;
      const pct = Math.min(90, Math.round((elapsedMs / TOTAL_ESTIMADO_MS) * 100));
      setOcrProgress(pct);
    }, 250);
  };

  // Limpiar al desmontar
  useEffect(() => {
    return () => limpiarTimers();
  }, []);

  // Limpiar al cerrar
  const handleClose = () => {
    limpiarTimers();
    setArchivo(null);
    setPreview(null);
    setOcrPhase('validating');
    setOcrProgress(0);
    onClose();
  };

  // Manejar drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
  }, []);

  // Manejar selección de archivo
  const handleFileSelected = (file: File) => {
    // Validar tipo
    const validTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];
    if (!validTypes.includes(file.type)) {
      alert('Tipo de archivo no válido. Use JPG, PNG, WebP o PDF');
      return;
    }

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 10MB');
      return;
    }

    setArchivo(file);

    // Crear preview si es imagen
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null); // Para PDFs no mostramos preview
    }
  };

  // Procesar OCR
  const handleProcesar = async () => {
    if (!archivo) return;

    iniciarAnimacionOCR();

    try {
      const result = await procesarOCRMutation.mutateAsync(archivo);

      limpiarTimers();

      if (result.success && result.data) {
        setOcrPhase('complete');
        setOcrProgress(100);
        // Breve pausa para mostrar "Completado" antes de cerrar
        setTimeout(() => {
          onDataExtracted(result.data!, result.imagen_url);
          handleClose();
        }, 700);
      } else {
        // Error en el procesamiento
        setOcrPhase('validating');
        setOcrProgress(0);
        console.error('Error OCR:', result.error);
      }
    } catch (error: any) {
      limpiarTimers();
      setOcrPhase('validating');
      setOcrProgress(0);
      console.error('Error al procesar OCR:', error);
    }
  };

  if (!isOpen) return null;

  const isLoading = procesarOCRMutation.isPending;
  const error = procesarOCRMutation.error as Error | null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[70] p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileImage className="w-6 h-6 text-blue-500 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Cargar Factura con OCR
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-40"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Zona de upload */}
          {!archivo && !isLoading && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
                transition-colors
                ${
                  isDragOver
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Arrastra una imagen o PDF aqui
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                o haz click para seleccionar
              </p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                }}
                className="hidden"
                id="file-upload-ocr"
              />
              <label
                htmlFor="file-upload-ocr"
                className="inline-block px-5 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg
                           hover:bg-blue-700 dark:hover:bg-blue-600 cursor-pointer transition-colors font-medium"
              >
                Seleccionar archivo
              </label>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                Formatos aceptados: JPG, PNG, WebP, PDF (max. 10MB)
              </p>
            </div>
          )}

          {/* Preview y procesamiento */}
          {archivo && (
            <div className="space-y-4">
              {/* Informacion del archivo */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <FileImage className="w-10 h-10 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {archivo.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(archivo.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                {!isLoading && (
                  <button
                    onClick={() => {
                      setArchivo(null);
                      setPreview(null);
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label="Quitar archivo"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Preview de imagen */}
              {preview && !isLoading && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <img
                    src={preview}
                    alt="Preview de la factura"
                    className="w-full h-auto max-h-80 object-contain bg-gray-100 dark:bg-gray-800"
                  />
                </div>
              )}

              {/* Barra de progreso OCR */}
              {isLoading && (
                <OCRProgressIndicator phase={ocrPhase} progress={ocrProgress} />
              )}

              {/* Estado completado (breve flash antes de cerrar) */}
              {ocrPhase === 'complete' && !isLoading && (
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="font-medium text-green-900 dark:text-green-100">
                    Datos extraidos exitosamente
                  </p>
                </div>
              )}

              {/* Error */}
              {error && !isLoading && (
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900 dark:text-red-100">
                      Error al procesar la imagen
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      {error instanceof Error
                        ? error.message
                        : 'Error desconocido'}
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                      Puedes intentar de nuevo o continuar con ingreso manual
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg
                       transition-colors disabled:opacity-40"
          >
            Cancelar
          </button>
          {archivo && !isLoading && !procesarOCRMutation.isSuccess && (
            <button
              onClick={handleProcesar}
              className="px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg
                         hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center gap-2 font-medium"
            >
              <FileImage className="w-4 h-4" />
              Procesar con IA
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadFacturaOCR;
